import React, { useEffect, useRef, useState, useCallback } from "react";
import client from '../../contentfulClientToken';
import '../ImageScroller.scss';
import { getImageUrl as getDefaultImageUrl } from '../../utils/imageUtils';
 

/**
 * Generic scroller for keyword/tag pages (people, japan, water, etc.).
 * Mirrors YearTag behavior but allows custom image URL resolver and viewer class.
 *
 * Props:
 * - tagId: Contentful asset tag id to filter assets
 * - handwritingSrc: path to handwriting image to display at bottom
 * - handwritingAlt: alt text for handwriting image
 * - getUrlFn?: optional function to resolve the display URL (defaults to imageUtils.getImageUrl)
 * - fixedViewerClassName?: optional class for the fixed viewer (defaults to 'fixed-image-viewer')
 */
export default function KeywordsTag({
    tagId,
    handwritingSrc,
    handwritingAlt,
    getUrlFn = getDefaultImageUrl,
    fixedViewerClassName = 'fixed-image-viewer',
    yearsIsVisible,
    monthsIsVisible,
    keywordsIsVisible,
}) {
    const [images, setImages] = useState([]);
    const [activeImage, setActiveImage] = useState('');
    const [activeIndex, setActiveIndex] = useState(0);
    const [duplicatedImages, setDuplicatedImages] = useState([]);

    const imageRefs = useRef([]);
    const lastScrollY = useRef(0);
    const pendingScrollYRef = useRef(0);
    const tickingRef = useRef(false);
    const containerRef = useRef(null);
    const topSentinelRef = useRef(null);
    const bottomSentinelRef = useRef(null);
    const imagesRef = useRef([]);
    const activeImageRef = useRef('');
    const duplicatedImagesRef = useRef([]);
    const handleImageLoad = useCallback(() => {
        // no-op image onLoad handler
    }, []);

    // Keep refs in sync with state
    useEffect(() => {
        imagesRef.current = images;
        activeImageRef.current = activeImage;
        duplicatedImagesRef.current = duplicatedImages;
    }, [images, activeImage, duplicatedImages]);

    // Initialize when images load
    useEffect(() => {
        if (images.length > 0) {
            setDuplicatedImages(images);
            const firstImageUrl = images[0] || '';
            setActiveImage(firstImageUrl ? getUrlFn(firstImageUrl) : '');
            setActiveIndex(0);
        }
    }, [images, getUrlFn]);

    const getOriginalIndex = useCallback((duplicatedIndex) => {
        const originalLength = imagesRef.current.length || 0;
        return duplicatedIndex % originalLength;
    }, []);

    const handleScroll = useCallback(() => {
        // Throttle to next animation frame and keep only lightweight work here
        pendingScrollYRef.current = window.scrollY;
        if (tickingRef.current) return;
        tickingRef.current = true;

        requestAnimationFrame(() => {
            const currentScrollY = pendingScrollYRef.current;
            lastScrollY.current = currentScrollY;
            tickingRef.current = false;
        });
    }, []);

    useEffect(() => {
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [handleScroll]);

    // Avoid browser restoring previous scroll, and ensure start at top
    useEffect(() => {
        const { history } = window;
        const prev = history && history.scrollRestoration;
        if (history && 'scrollRestoration' in history) {
            history.scrollRestoration = 'manual';
        }
        // Ensure top on first mount
        requestAnimationFrame(() => {
            window.scrollTo(0, 0);
        });
        return () => {
            if (history && 'scrollRestoration' in history && prev) {
                history.scrollRestoration = prev;
            }
        };
    }, []);

    // IntersectionObserver to set active image based on proximity to top
    useEffect(() => {
        if (!imageRefs.current || imageRefs.current.length === 0) return;

        const observer = new IntersectionObserver(
            (entries) => {
                // Pick the intersecting image closest to the top of the viewport
                const best = entries
                    .filter((e) => e.isIntersecting)
                    .reduce((closest, e) => {
                        if (!closest) return e;
                        const a = Math.abs(e.boundingClientRect.top);
                        const b = Math.abs(closest.boundingClientRect.top);
                        return a < b ? e : closest;
                    }, null);

                if (!best) return;
                const indexAttr = best.target.getAttribute('data-index');
                if (indexAttr == null) return;
                const idx = Number(indexAttr);
                const item = duplicatedImagesRef.current[idx];
                if (!item) return;

                const newUrl = getUrlFn(item);
                if (newUrl !== activeImageRef.current) {
                    setActiveImage(newUrl);
                }
                setActiveIndex(getOriginalIndex(idx));
            },
            {
                root: null,
                rootMargin: '0px 0px -90% 0px',
                threshold: 0,
            }
        );

        imageRefs.current.forEach((imgEl) => {
            if (imgEl) observer.observe(imgEl);
        });

        return () => observer.disconnect();
    }, [duplicatedImages, getOriginalIndex, getUrlFn]);

    // IntersectionObservers for infinite duplication using sentinels
    useEffect(() => {
        const topEl = topSentinelRef.current;
        const bottomEl = bottomSentinelRef.current;
        if (!topEl && !bottomEl) return;

        let lastAppendAt = 0;
        let lastPrependAt = 0;
        const minPixelGap = 800;

        const onIntersect = (entries) => {
            const nowY = window.scrollY;
            entries.forEach((entry) => {
                if (!entry.isIntersecting) return;
                if (entry.target === bottomEl) {
                    if (Math.abs(nowY - lastAppendAt) < minPixelGap) return;
                    lastAppendAt = nowY;
                    setDuplicatedImages(prev => [...prev, ...imagesRef.current]);
                } else if (entry.target === topEl) {
                    if (Math.abs(nowY - lastPrependAt) < minPixelGap) return;
                    lastPrependAt = nowY;
                    setDuplicatedImages(prev => [...imagesRef.current, ...prev]);
                }
            });
        };

        const sentinelObserver = new IntersectionObserver(onIntersect, {
            root: null,
            rootMargin: '500px',
            threshold: 0,
        });

        if (bottomEl) sentinelObserver.observe(bottomEl);
        if (topEl) sentinelObserver.observe(topEl);

        return () => sentinelObserver.disconnect();
    }, [images]);

    // Fetch images for the given tag
    useEffect(() => {
        const query = {
            content_type: 'month',
            limit: 500, // to be able to fetch all the months
            order: '-sys.createdAt', // to be able to fetch the latest months first
        };
        client.getEntries(query)
            .then(response => {
                if (response.items.length > 0 && response.includes && response.includes.Asset) {
                    const filteredAssets = response.includes.Asset.filter(asset =>
                        Array.isArray(asset?.metadata?.tags) && asset.metadata.tags.some(tag => tag.sys.id === tagId)
                    );

                    const sortedAssets = filteredAssets.sort((a, b) =>
                        new Date(b.sys.createdAt) - new Date(a.sys.createdAt)
                    );

                    const imageUrls = sortedAssets.map(asset => asset.fields.file.url);
                    setImages(imageUrls);
                    // Reset scroll to top when changing tag/images
                    requestAnimationFrame(() => {
                        window.scrollTo(0, 0);
                    });
                }
            })
            .catch(error => {
                console.error(error);
            });
    }, [tagId]);

    return (
        <div className="container">
            <div className="picture-number">
                <p>{Math.max(1, activeIndex + 1)}</p>
            </div>

            <div className={fixedViewerClassName}>
                {activeImage ? (
                    <img src={activeImage} alt="Currently visible" />
                ) : (
                    <div className="fixed-image-loading">loading</div>
                )}
            </div>

            <div className="scroll-container" ref={containerRef}>
                <div ref={topSentinelRef} style={{ height: 1 }} />
                {duplicatedImages.map((imageUrl, index) => (
                    <img
                        key={`${imageUrl}-${index}`}
                        src={getUrlFn(imageUrl)}
                        loading="lazy"
                        onLoad={handleImageLoad}
                        data-index={index}
                        ref={(el) => (imageRefs.current[index] = el)}
                        alt="Timothée Lambrecq photography"
                        className="scroll-image"
                    />
                ))}
                <div ref={bottomSentinelRef} style={{ height: 1 }} />
            </div>

            {!(yearsIsVisible || monthsIsVisible || keywordsIsVisible) && (
                <div className="handwriting-title">
                    <img src={handwritingSrc} alt={handwritingAlt || 'handwriting'} />
                </div>
            )}
        </div>
    );
}

